import { useCallback, useMemo, useState } from "react";

import { useRecommendedJobs, useSavedJobs } from "@/app/services/create-resume/queries";
import { ResumeDataService } from "@/app/services/create-resume/resume-data";

interface UseSaveJobReturn {
	/** Whether this job is already in the reader's saved list. */
	isSaved: boolean;
	/** True while the save request is in flight, so the button can disable itself. */
	saving: boolean;
	saveJob: () => void;
}

/**
 * Save a job for later, from wherever it is listed.
 *
 * Shared by every surface that offers a save control, so the saved list and the recommendations
 * are refreshed the same way each time and a row cannot disagree with a card about whether the
 * reader has already saved the role.
 */
export function useSaveJob(jobId?: string): UseSaveJobReturn {
	const jobService = useMemo(() => new ResumeDataService(), []);
	const [saving, setSaving] = useState(false);

	const { refetch: refetchRecommendedJobs } = useRecommendedJobs();
	const { data: savedJobs, refetch: refetchSavedJobs } = useSavedJobs();

	const isSaved = useMemo(() => {
		if (!jobId || !Array.isArray(savedJobs)) return false;
		return savedJobs.some((savedJob: { _id?: string }) => savedJob._id === jobId);
	}, [savedJobs, jobId]);

	const saveJob = useCallback(() => {
		if (!jobId) return;

		setSaving(true);
		jobService.saveJobInteraction(
			jobId,
			{ interactionType: "Saved" },
			() => {
				refetchRecommendedJobs();
				refetchSavedJobs();
				setSaving(false);
			},
			() => {
				setSaving(false);
			},
		);
	}, [jobId, jobService, refetchRecommendedJobs, refetchSavedJobs]);

	return { isSaved, saving, saveJob };
}
